/**
 * InlineEdit - Inline title editing component
 *
 * Double-click to edit, blur or Enter to commit.
 * Naked UI - minimal styling.
 */

import React, { useState, useEffect, useRef } from 'react';

export interface InlineEditProps {
  value: string;
  onCommit: (newValue: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

export function InlineEdit({ value, onCommit, placeholder = 'Senza titolo', style }: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync tempValue when value prop changes
  useEffect(() => {
    setTempValue(value);
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleCommit = () => {
    setEditing(false);
    const trimmed = tempValue.trim();
    if (trimmed !== value) {
      onCommit(trimmed || placeholder);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCommit();
    } else if (e.key === 'Escape') {
      setEditing(false);
      setTempValue(value);
    }
  };

  if (!editing) {
    return (
      <span
        onDoubleClick={() => setEditing(true)}
        style={{
          cursor: 'text',
          minWidth: '3rem',
          ...style,
        }}
        title="Double click to edit"
      >
        {value || placeholder}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={tempValue}
      onChange={(e) => setTempValue(e.target.value)}
      onBlur={handleCommit}
      onKeyDown={handleKeyDown}
      style={{
        border: '1px solid var(--brand, #3b82f6)',
        borderRadius: '0.25rem',
        padding: '0.125rem 0.25rem',
        fontSize: 'inherit',
        fontFamily: 'inherit',
        outline: 'none',
        minWidth: '10rem',
        ...style,
      }}
    />
  );
}
